"use client";

import React from "react";
import { useAppContext } from "@/context/AppContext";
import { useAuthContext } from "@/context/AuthContext";
import { useParentAudience } from "@/hooks/useParentAudience";
import { useLayoutMode } from "@/hooks/useLayoutMode";
import HeroPremium from "./HeroPremium";
import SubscriptionCTA from "@/components/SubscriptionCTA";
import { useRouter } from "next/navigation";
import Container from "../Container";
import { FEATURE_CARDS } from "@/lib/featureConstants";
import Image from "next/image";
import posthog from "posthog-js";

function FeatureIconsGrid() {
  const router = useRouter();

  const renderIcon = (card: (typeof FEATURE_CARDS)[number], index: number) => (
    <button
      key={index}
      onClick={() => router.push("/uitproberen")}
      className="flex flex-col items-center gap-2 cursor-pointer hover:scale-105 transition-transform"
    >
      <div
        className={`w-12 h-12 md:w-14 md:h-14 rounded-xl shadow-lg flex items-center justify-center text-white ${card.color}`}
      >
        <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center">
          {card.icon}
        </div>
      </div>
      <span className="text-xs md:text-sm font-semibold text-gray-700">
        {card.title}
      </span>
    </button>
  );

  return (
    <div className="grid grid-cols-3 gap-y-3 w-fit mx-auto">
      {FEATURE_CARDS.map((card, i) => renderIcon(card, i))}
    </div>
  );
}

type FeatureCardSize = "sm" | "md" | "lg";

function FeatureCard({ card, className, size = "md" }: { card: (typeof FEATURE_CARDS)[number]; className?: string; size?: FeatureCardSize }) {
  const sizes = {
    sm: { card: "p-3 rounded-xl min-w-[76px]", icon: "w-10 h-10 rounded-xl", inner: "w-5 h-5", text: "text-[11px]" },
    md: { card: "p-4 rounded-2xl min-w-[88px]", icon: "w-12 h-12 rounded-xl", inner: "w-6 h-6", text: "text-xs" },
    lg: { card: "p-5 rounded-2xl min-w-[100px]", icon: "w-14 h-14 rounded-2xl", inner: "w-7 h-7", text: "text-sm" },
  };
  const s = sizes[size];
  return (
    <div className={`absolute bg-white ${s.card} flex flex-col items-center gap-2 ${className ?? ""}`}>
      <div className={`${s.icon} flex items-center justify-center text-white ${card.color}`}>
        <div className={`${s.inner} flex items-center justify-center`}>{card.icon}</div>
      </div>
      <span className={`${s.text} font-semibold text-gray-700 whitespace-nowrap`}>{card.title}</span>
    </div>
  );
}

function ShowcaseContent() {
  return (
    <>
      {/* White shapes layer — unified info border via drop-shadow on composite silhouette */}
      <div
        className="absolute inset-0 z-[5]"
        style={{
          filter:
            "drop-shadow(2px 0 0 #3e8ed0) drop-shadow(-2px 0 0 #3e8ed0) drop-shadow(0 2px 0 #3e8ed0) drop-shadow(0 -2px 0 #3e8ed0)",
        }}
      >
        {/* Center blob */}
        <div className="absolute left-[50%] top-[46%] -translate-x-1/2 -translate-y-1/2 bg-white rounded-3xl w-[232px] h-[163px]" />

        {/* Feature cards */}
        <FeatureCard card={FEATURE_CARDS[0]} size="sm" className="top-[18px] left-[48px] rotate-[-3deg]" />
        <FeatureCard card={FEATURE_CARDS[2]} size="sm" className="top-[14px] right-[88px] rotate-[3deg]" />
        <FeatureCard card={FEATURE_CARDS[4]} size="md" className="top-[44%] -translate-y-1/2 left-[14px] rotate-[-4deg]" />
        <FeatureCard card={FEATURE_CARDS[3]} size="md" className="top-[44%] -translate-y-1/2 right-[12px] rotate-[4deg]" />
        <FeatureCard card={FEATURE_CARDS[1]} size="sm" className="bottom-[38px] left-[48%] -translate-x-1/2 rotate-[1deg]" />
        <FeatureCard card={FEATURE_CARDS[5]} size="sm" className="bottom-[56px] right-[44px] rotate-[-5deg]" />
      </div>

      {/* Layer 3: Book images only — on top of everything */}
      <div
        className="absolute left-[50%] top-[46%] -translate-x-1/2 -translate-y-1/2 z-20 flex items-end justify-center -space-x-8 pointer-events-none"
      >
        <div className="relative z-0 rotate-[-4deg] rounded-lg overflow-hidden">
          <Image
            src="/getalenruimte.png"
            alt="Getal & Ruimte"
            width={300}
            height={345}
            sizes="110px"
            className="w-[110px] h-auto block"
          />
        </div>
        <div className="relative z-10 rotate-[4deg] rounded-lg overflow-hidden">
          <Image
            src="/modernewiskunde.png"
            alt="Moderne Wiskunde"
            width={300}
            height={345}
            sizes="110px"
            className="w-[110px] h-auto block"
          />
        </div>
      </div>
    </>
  );
}

/**
 * Responsive wrapper that scales BookAndFeaturesShowcase to fill its
 * parent container (which should have explicit width/height, e.g. via h-2/3).
 * Uses ResizeObserver to compute the right CSS scale so the 380×360 base
 * layout always fills exactly the available space.
 */
/**
 * Responsive wrapper that scales BookAndFeaturesShowcase to fill 2/3
 * of its parent's HEIGHT. Measures the parent via ResizeObserver so it
 * works in any flex/grid layout without percentage-height or
 * aspect-ratio CSS tricks that break flex containers.
 *
 * Usage: place directly inside the flex-1 bg-achtergrond area.
 * No wrapper div needed — the component handles sizing internally.
 */
export function ResponsiveShowcase({ heightFraction = 2 / 3 }: { heightFraction?: number }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        // Target: showcase should fill heightFraction of container height
        const targetH = height * heightFraction;
        // Scale proportionally, capped by width so it never overflows
        setScale(Math.min(width / 380, targetH / 360));
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [heightFraction]);

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center overflow-hidden">
      <div
        className="relative w-[380px] h-[360px] shrink-0"
        style={{ transform: `scale(${scale})`, opacity: scale > 0 ? 1 : 0 }}
      >
        <ShowcaseContent />
      </div>
    </div>
  );
}

export function BookAndFeaturesShowcase({ mobile = false }: { mobile?: boolean }) {
  // Base layout is 380×360. Scales: mobile=0.65x, md=0.79x, lg=1x, xl=1.105x.
  if (mobile) {
    return (
      <div className="w-[304px] h-[288px]">
        <div className="relative w-[380px] h-[360px] origin-top-left scale-[0.80]">
          <ShowcaseContent />
        </div>
      </div>
    );
  }

  return (
    <div className="w-[300px] h-[284px] lg:w-[380px] lg:h-[360px] xl:w-[420px] xl:h-[396px]">
      <div
        className="relative w-[380px] h-[360px] origin-top-left scale-[0.79] lg:scale-100 xl:scale-[1.105]"
      >
        <ShowcaseContent />
      </div>
    </div>
  );
}

export default function Hero() {
  const { isMobile } = useAppContext();
  const { isPremium } = useAuthContext();
  const isParent = useParentAudience();
  const { isNativeIOS } = useLayoutMode();
  const router = useRouter();
  const hideUitproberen = isParent && isNativeIOS;

  if (isPremium) {
    return <HeroPremium />;
  }

  const subtitle = isParent
    ? "WisWiz helpt je kind wiskunde écht te begrijpen met gepersonaliseerde uitleg die direct aansluit bij iedere opgave en hoofdstuk van hun lesboek."
    : "WisWiz helpt je wiskunde écht te begrijpen met gepersonaliseerde uitleg die direct aansluit bij iedere opgave en hoofdstuk van jouw lesboek.";

  // Mobile layout
  if (isMobile) {
    return (
      <section className="px-6 relative flex items-center justify-center pt-6 pb-8">
        <div className="w-full max-w-xl mx-auto">
          <div className="flex flex-col space-y-4">
            <h1 className="text-3xl font-bold leading-tight text-gray-900 text-center">
              Wiskundebijles die{" "}
              <span className="text-info">altijd beschikbaar is</span>
            </h1>

            <p className="text-base text-gray-900 text-center !mt-3">
              {subtitle}
            </p>

            {/* Books + Feature cards — same layout as desktop, scaled down */}
            <div className="flex justify-center -mb-4 -mt-2">
              <BookAndFeaturesShowcase mobile />
            </div>

            {/* CTA */}
            <div className="flex flex-col items-center gap-3 !mt-4">
              <SubscriptionCTA variant="outline" className="bg-white w-full max-w-xs" />
              {!hideUitproberen && (
                <button
                  onClick={() => {
                    posthog.capture("hero_test_cta_clicked", {
                      location: "hero_mobile",
                      cta: "ontdek_wiswiz",
                      audience: isParent ? "parent" : "student"
                    });
                    router.push("/uitproberen");
                  }}
                  className="font-bold text-base py-3 px-6 rounded-lg border-2 border-info text-gray-900 bg-white hover:bg-info/10 transition-colors cursor-pointer w-full max-w-xs"
                >
                  Ontdek WisWiz
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Desktop layout
  return (
    <section
      className="px-4 lg:px-8 relative flex flex-col items-center justify-center py-12 md:py-16 lg:py-20"
      aria-label="WisWiz hero"
    >
      <Container>
        <div className="flex flex-col md:flex-row justify-between items-start gap-8 lg:gap-16 w-full">
          {/* Left side — Text + CTAs */}
          <div className="md:flex-1 text-center md:text-left space-y-5 max-w-xl">
            <h1 className="text-4xl font-bold leading-tight text-gray-900 md:text-[2.5rem] md:leading-tight lg:text-[3.25rem] lg:leading-tight">
              Wiskundebijles die{" "}
              <span className="text-info">altijd beschikbaar is</span>
            </h1>

            <p className="text-base text-gray-900 md:text-lg md:border-l-4 md:border-info md:pl-4">
              {subtitle}
            </p>

            {/* CTA section */}
            <div className="pt-2 md:pt-3 flex flex-wrap justify-center md:justify-start gap-3">
              <SubscriptionCTA variant="outline" className="bg-white" />
              {!hideUitproberen && (
                <button
                  type="button"
                  onClick={() => {
                    posthog.capture("hero_test_cta_clicked", {
                      location: "hero_desktop",
                      cta: "probeer_wiswiz",
                      audience: isParent ? "parent" : "student"
                    });
                    router.push("/uitproberen");
                  }}
                  className="group flex cursor-pointer items-center gap-2 font-bold text-gray-900 text-base border-2 border-info py-3 px-6 rounded-lg bg-white hover:bg-info/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/40"
                  aria-label="Probeer WisWiz"
                >
                  Probeer WisWiz
                </button>
              )}
            </div>
          </div>

          {/* Right side — Books + Features */}
          <div className="hidden md:flex md:flex-shrink-0 md:items-center md:justify-end">
            <BookAndFeaturesShowcase />
          </div>
        </div>
      </Container>
    </section>
  );
}
